// react
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui icons
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForward';

// material ui
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

// feature-components
import EnergyGroupTypeAvatar from '@/features/energyxplorer/components/energygroup/EnergyGroupTypeAvatar';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

const LinkedEnergyGroupsCard = ({ linkedEnergyGroups }) => {
  const { t } = useTranslation('portfolio-analysis');
  const navigate = useNavigate();
  const account = useSelector(getSelectedAccount);

  if (!linkedEnergyGroups || linkedEnergyGroups.length === 0) {
    return null;
  }

  // Filter groups where type contains 'portfolio' and split into 3 lists by type
  const energyGroups = linkedEnergyGroups.filter(
    (group) => !group.type?.toLowerCase().includes('portfolio'),
  );

  // Split into three categories based on type
  const powerGroups = energyGroups.filter((group) =>
    group.type?.toLowerCase().includes('power'),
  );
  const heatGroups = energyGroups.filter((group) =>
    group.type?.toLowerCase().includes('heat'),
  );
  const waterGroups = energyGroups.filter((group) =>
    group.type?.toLowerCase().includes('water'),
  );

  const handleNavigate = (energyGroupId) => {
    navigate({
      to: `/${account?.AccountId}/energyxplorer/deepdive/${energyGroupId}/consumption-overview`,
    });
  };

  const renderGroupCard = (groups, title) => {
    if (groups.length === 0) return null;
    const icon = <EnergyGroupTypeAvatar type={groups[0].type} />;
    return (
      <Grid
        size={{
          xs: 12,
          lg: 4,
        }}
      >
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 250,
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                mb: 2,
              }}
            >
              {icon}
              <Typography variant="h6" fontWeight="bold">
                {title}
              </Typography>
            </Box>
            <Stack spacing={1}>
              {groups.map((group) => (
                <Button
                  key={group.energyGroupId}
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={() => handleNavigate(group.energyGroupId)}
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    justifyContent: 'space-between',
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="body2" noWrap>
                    {group.name || `Energy Group ${group.energyGroupId}`}
                  </Typography>
                </Button>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Grid container spacing={2}>
      {renderGroupCard(
        powerGroups,
        t('linked_energy_groups.power_title', { defaultValue: 'El' }),
      )}
      {renderGroupCard(
        heatGroups,
        t('linked_energy_groups.heat_title', { defaultValue: 'Varme' }),
      )}
      {renderGroupCard(
        waterGroups,
        t('linked_energy_groups.water_title', { defaultValue: 'Vand' }),
      )}
    </Grid>
  );
};

export default LinkedEnergyGroupsCard;
